/**
 * Section Visibility Configuration
 * Controls which sections are visible based on environment variables
 * Similar to the VITE_COMING_SOON_ENABLED pattern
 */

export interface SectionVisibilityConfig {
  // Home page sections
  heroSection: boolean;
  statsSection: boolean;
  servicesPreview: boolean;
  portfolioSection: boolean;
  testimonialsSection: boolean;
  githubProjectsSection: boolean;
  
  // Navigation items
  homeNavItem: boolean;
  servicesNavItem: boolean;
  technologiesNavItem: boolean;
  aboutNavItem: boolean;
  contactNavItem: boolean;
  
  // Floating Action Menu items
  contactFloatingButton: boolean;
  servicesFloatingButton: boolean;
  technologiesFloatingButton: boolean;
  languageFloatingButton: boolean;
}

// Note: All sections are visible by default unless explicitly hidden via environment variables

/**
 * Get section visibility configuration from environment variables
 * Environment variables follow the pattern: VITE_HIDE_[SECTION_NAME]=true
 * If a variable is set to 'true', the section will be hidden
 */
export const getSectionVisibility = (): SectionVisibilityConfig => {
  return {
    // Home page sections
    heroSection: import.meta.env.VITE_HIDE_HERO_SECTION !== 'true',
    statsSection: import.meta.env.VITE_HIDE_STATS_SECTION !== 'true',
    servicesPreview: import.meta.env.VITE_HIDE_SERVICES_PREVIEW !== 'true',
    portfolioSection: import.meta.env.VITE_HIDE_PORTFOLIO_SECTION !== 'true',
    testimonialsSection: import.meta.env.VITE_HIDE_TESTIMONIALS_SECTION !== 'true',
    githubProjectsSection: import.meta.env.VITE_HIDE_GITHUB_PROJECTS_SECTION !== 'true',
    
    // Navigation items
    homeNavItem: import.meta.env.VITE_HIDE_HOME_NAV !== 'true',
    servicesNavItem: import.meta.env.VITE_HIDE_SERVICES_NAV !== 'true',
    technologiesNavItem: import.meta.env.VITE_HIDE_TECHNOLOGIES_NAV !== 'true',
    aboutNavItem: import.meta.env.VITE_HIDE_ABOUT_NAV !== 'true',
    contactNavItem: import.meta.env.VITE_HIDE_CONTACT_NAV !== 'true',
    
    // Floating Action Menu items
    contactFloatingButton: import.meta.env.VITE_HIDE_CONTACT_FLOATING !== 'true',
    servicesFloatingButton: import.meta.env.VITE_HIDE_SERVICES_FLOATING !== 'true',
    technologiesFloatingButton: import.meta.env.VITE_HIDE_TECHNOLOGIES_FLOATING !== 'true',
    languageFloatingButton: import.meta.env.VITE_HIDE_LANGUAGE_FLOATING !== 'true',
  };
};

/**
 * Hook to get section visibility configuration
 */
export const useSectionVisibility = () => {
  return getSectionVisibility();
};

export default getSectionVisibility;
